import {
  isPlatformBrowser,
  Location,
  NgClass,
  NgOptimizedImage
} from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  inject,
  input,
  PLATFORM_ID,
  Renderer2,
  signal,
  viewChild,
  DOCUMENT
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  Router,
  RouterLink
} from '@angular/router';
import { NgxTolgeeModule } from '@tolgee/ngx';

import { Activity } from '../../../../core/model/activity.model';
import { emulateClick } from '../core/a11y';
import { AutoCloseDirective } from '../shared/directives/auto-close.directive';
import { HoverGradientGreenDirective } from '../shared/directives/hover-gradient-green.directive';
import { HoverGradientDirective } from '../shared/directives/hover-gradient.directive';
import { Category } from '../shared/model/category.model';
import { ProductGroup } from '../shared/model/product-group.model';
import { Product } from '../shared/model/product.model';
import { Universe } from '../shared/model/universe.model';
import { ByPassPipe } from '../shared/pipes/by-pass.pipe';
import { AuthService } from '../shared/services/auth.service';
import { CategoryService } from '../shared/services/category.service';
import { GlobalService } from '../shared/services/globals.service';
import { ProductService } from '../shared/services/product.service';
import { SiteService } from '../shared/services/site.service';
import { UniverseService } from '../shared/services/universe.service';
import { ProductCardComponent } from './product-card/product-card.component';

@Component({
  selector: 'app-products',
  imports: [
    HoverGradientDirective,
    NgOptimizedImage,
    NgxTolgeeModule,
    AutoCloseDirective,
    NgClass,
    ProductCardComponent,
    RouterLink,
    ByPassPipe,
    HoverGradientGreenDirective
  ],
  templateUrl: './products.component.html'
})
export class ProductsComponent {
  readonly emulateClick = emulateClick;
  route = inject(ActivatedRoute);
  router = inject(Router);
  location = inject(Location);
  universeService = inject(UniverseService);
  authService = inject(AuthService);
  categoryService = inject(CategoryService);
  productService = inject(ProductService);
  globalService = inject(GlobalService);
  siteService = inject(SiteService);
  redirectUrl = input('');
  content = input<'all' | 'minimal' | (Product | ProductGroup)[]>('all')
  scrollContainer = viewChild.required<ElementRef>('scrollContainer');
  center = inject(SiteService).center;
  sort = signal<'inc' | 'dec' | null>(null);
  universe = signal<Universe | null>(null);
  activity = signal<Activity | null>(null);
  category = signal<Category | null>(null);
  promo = signal(false);
  giftMode = signal(false);
  products = computed(() => {
    if (!this.siteService.center()?.enabled && !this.authService.user()?.admin) {
      return [];
    }
    const content = this.content();
    if (Array.isArray(content)) {
      return content;
    }
    const universe = this.universe();
    const category = this.category();
    const activity = this.activity();
    const promo = this.promo();
    const products =
      this.productService.ofCurrentCenter()
        .filter(product => {
          if (universe) {
            const productUniverse = product.universes.find(u => u.id === universe.id);
            if (!productUniverse) {
              return false;
            }
            if (activity && !productUniverse.activities.includes(activity.id)) {
              return false;
            }
          }
          if (category && !product.categories.includes(category.id)) {
            return false;
          }
          if (promo) {
            if (product instanceof ProductGroup &&
              product.products.every(p => !this.siteService.price(p.id).promo)) {
              return false
            }
            if (product instanceof Product && !this.siteService.price(product.id).promo) {
              return false;
            }
          }
          return true;
        });
    let sortFn: (a: Product | ProductGroup, b: Product | ProductGroup) => number;
    switch (this.sort()) {
    case 'inc': {
      sortFn = (a, b): number => this.minPrice(a) - this.minPrice(b);
      break;
    }
    case 'dec': {
      sortFn = (a, b): number => this.minPrice(b) - this.minPrice(a);
      break;
    }
    default:
      sortFn = (a, b): number => this.siteService.index(a.id) - this.siteService.index(b.id);
      break;
    }
    products.sort(sortFn)
    return content === 'minimal' ? products.slice(0, 6) : products;
  });
  private renderer = inject(Renderer2);
  private document = inject(DOCUMENT);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  constructor() {
    this.renderer.setProperty(
      this.document.documentElement.style, `--color-store-fg`, 'rgb(240,112,102)');
    this.route.queryParamMap
      .pipe(takeUntilDestroyed())
      .subscribe(map => {
        this.giftMode.set(map.get('gift') === 'true');
        const universeName = map.get('universe');
        if (universeName) {
          const universe = this.universeService.byName(universeName);
          this.switchUniverse(universe)
        }
        const activityName = map.get('activity');
        if (activityName) {
          const activity = this.universe()?.activities.find(a => a.name === activityName) ?? null;
          this.switchActivity(activity)
        }
        const sort = map.get('sort') as 'inc' | 'dec' | null;
        if (sort) {
          this.switchSort(sort ?? null);
        }
        const categoryName = map.get('category');
        if (categoryName) {
          const category = this.categoryService.ofCurrentCenter().find(c => c.name === categoryName) ?? null;
          this.switchCategory(category);
        }
        if (map.get('promo') === 'true') {
          this.togglePromo(true);
        }
      });
  }

  togglePromo(forceTrue = false): void {
    this.promo.update(u => forceTrue || !u);
    this.universe.set(null);
    this.activity.set(null);
    this.category.set(null);
    this.updateRoute();
  }

  switchUniverse(universe: Universe | null): void {
    this.universe.update(u => u === universe ? null : universe);
    this.activity.set(null);
    this.category.set(null);
    this.promo.set(false);
    this.updateRoute()
  }

  switchActivity(activity: Activity | null): void {
    this.activity.update(a => a === activity ? null : activity);
    this.category.set(null);
    this.promo.set(false);
    this.updateRoute();
  }

  switchCategory(category: Category | null): void {
    this.category.update(c => c === category ? null : category);
    this.universe.set(null);
    this.activity.set(null);
    this.promo.set(false);
    this.updateRoute();
  }

  switchSort(sort: 'inc' | 'dec' | null): void {
    this.sort.update(a => a === sort ? null : sort);
    this.updateRoute();
  }

  categoryIcon(c: Category): string {
    if (this.giftMode()) {
      return `assets/icons/${c.id === this.category()?.id ? 'white' : 'green'}/${c.icon}`;
    } else {
      return `assets/icons/${c.id === this.category()?.id ? 'white' : 'blue'}/${c.icon}`;
    }
  }

  activityIcon(a: Activity): string {
    if (this.giftMode()) {
      return `assets/icons/${a.id === this.activity()?.id ? 'white' : 'green'}/${a.icon}`;
    } else {
      return `assets/icons/${a.id === this.activity()?.id ? 'white' : 'blue'}/${a.icon}`;
    }
  }

  scrollLeft(): void {
    this.scrollContainer().nativeElement.scrollLeft -= 500;
  }

  scrollRight(): void {
    this.scrollContainer().nativeElement.scrollLeft += 500;
  }

  title(): string {
    if (this.giftMode()) {
      return 'allGifts';
    }
    switch (this.content()) {
    case 'all':
      return 'theShopOf';
    case 'minimal':
      return 'mostPopular';
    default:
      return '';
    }
  }

  /**
   * Reflects the current filters into the URL without triggering a navigation.
   *
   * Never runs during server-side rendering: mutating the location there makes
   * `@angular/ssr` treat the render as a redirect, and because the CDN forwards
   * spaces as `+` while Angular re-serializes them as `%20`, any filter value
   * containing a space would redirect to itself indefinitely.
   * @returns Nothing.
   */
  private updateRoute(): void {
    if (!this.isBrowser) {
      return;
    }
    const url = this.router.createUrlTree([ '.' ], {
      relativeTo: this.route,
      queryParams: {
        sort: this.sort(),
        universe: this.universe()?.name,
        activity: this.activity()?.name,
        category: this.category()?.name,
        promo: this.promo() || undefined,
      },
      queryParamsHandling: 'merge'
    }).toString()
    this.location.replaceState(url);
  }

  private minPrice(product: Product | ProductGroup): number {
    let res;
    if (product instanceof Product) {
      const { price } = this.siteService.price(product.id);
      res = price;
    } else {
      res = product.products.reduce((min, curr) => {
        const { price } = this.siteService.price(curr.id);
        return Math.min(min, price);
      }, Number.POSITIVE_INFINITY)
    }
    return res;
  }
}
